'use client'
import Image, { StaticImageData } from 'next/image'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Slider, { Settings } from 'react-slick'

import islamicLogo from '@/assets/images/islamic-logo.png'
import sisterImg from '@/assets/images/sister.png'
import userImg from '@/assets/images/user-1.jpeg'
import { PublicTestimonialDto } from '@/server/public-api/types'
import { TGender } from '@/types'

import { TESTIMONIALS } from './utils'

const getFallbackImage = (gender: TGender) => (gender === 'FEMALE' ? sisterImg : userImg)

type TestimonialItem = {
  id: string
  name: string
  message: string | null
  image: string | null
  avatar: StaticImageData
}

// PF-83: the section now shows the academy's published CMS testimonials (like
// quranweb and maarij). The translated static quotes are only the onboarding
// fallback, used when nothing is published.
const Testimonial = ({ testimonials }: { testimonials: PublicTestimonialDto[] }) => {
  const slick = useRef<Slider | null>(null)
  const { t } = useTranslation()

  const items: TestimonialItem[] =
    testimonials.length > 0
      ? testimonials.map(testimonial => ({
          id: `cms-${testimonial.id}`,
          name: testimonial.name,
          message: testimonial.message,
          image: testimonial.image,
          avatar: getFallbackImage(testimonial.gender),
        }))
      : TESTIMONIALS.map(testimonial => ({
          id: `static-${testimonial.id}`,
          name: t(testimonial.nameKey),
          message: t(testimonial.textKey),
          image: null,
          avatar: testimonial.img,
        }))

  const settings: Settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    draggable: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 5000,
  }
  return (
    <section className='relative  h-[950px] pt-1'>
      <div className='container absolute left-1/2 top-0 h-[950px] -translate-x-1/2 bg-white opacity-60'>
        <h1 className='py-8 text-center text-[40px] text-primary-dark md:py-12 md:text-[64px]'>
          {t('testimonials.what_clients_say')}
        </h1>
        <h1 className='absolute bottom-0 left-1/2 -translate-x-1/2 pb-8 text-center text-[40px] text-primary-dark  md:whitespace-nowrap md:text-[64px]'>
          {t('testimonials.experienced_teachers')}
        </h1>
      </div>
      <section className='relative  mt-48 md:bg-secondary'>
        <div className='container flex flex-col justify-between gap-8 md:flex-row md:py-14'>
          <div className='md:w-1/2 '>
            <Slider {...settings} ref={slick}>
              {items.map(testimonial => (
                <div key={testimonial.id}>
                  <section className='min-h-[439px] max-w-[600px] rounded-[20px] bg-white px-5 pb-8 pt-4 text-center md:text-start'>
                    <div className='mb-6 flex items-end gap-1 md:mb-8'>
                      <figure>
                        <Image
                          src={testimonial.avatar}
                          width={100}
                          height={100}
                          alt={testimonial.name}
                          loading='lazy'
                          placeholder='blur'
                          className='size-20 shrink-0 object-cover md:size-24'
                        />
                      </figure>
                      <div className='space-y-2 self-center'>
                        <h4 className='text-[26px] font-semibold md:text-3xl'>{testimonial.name}</h4>
                      </div>
                    </div>

                    {testimonial.message ? (
                      <p className='text-base font-light md:text-2xl'>{testimonial.message}</p>
                    ) : testimonial.image ? (
                      <div className='w-full overflow-hidden rounded-lg'>
                        <Image
                          src={testimonial.image}
                          width={600}
                          height={400}
                          alt={testimonial.name}
                          loading='lazy'
                          className='h-auto w-full object-cover'
                        />
                      </div>
                    ) : null}
                  </section>
                </div>
              ))}
            </Slider>
          </div>
          <figure className='mx-auto hidden md:mx-0 md:block '>
            <Image
              src={islamicLogo}
              alt='islamic-logo'
              loading='lazy'
              placeholder='blur'
              className='size-[100px] md:size-[450px]'
            />
          </figure>
        </div>
      </section>{' '}
    </section>
  )
}

export default Testimonial
